import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Compass, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1526772662000-3f88f10405ff?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

export default function Register() {
  const { register, resendVerification, error, setError } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await register(name, email, password);
    setBusy(false);
    if (res.ok) setSent(true);
  };

  const resend = async () => {
    const ok = await resendVerification(email);
    toast[ok ? "success" : "error"](ok ? "Confirmation email re-sent" : "Could not resend right now");
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="register-check-email">
        <div className="w-full max-w-md text-center">
          <Link to="/" className="inline-flex items-center gap-2 mb-8"><Compass size={26} weight="duotone" className="text-[#2E523A]" /><span className="font-display text-xl font-bold">TravelNext</span></Link>
          <div className="h-16 w-16 rounded-2xl bg-[#2E523A] flex items-center justify-center mx-auto mb-6">
            <EnvelopeSimple size={32} weight="duotone" className="text-[#E8B291]" />
          </div>
          <h1 className="font-display text-3xl font-bold">Confirm your email</h1>
          <p className="text-[#63675E] mt-3">
            We've sent a confirmation link to <span className="font-semibold text-[#1A1C19]">{email}</span>.
            Click it to activate your account, then sign in.
          </p>
          <div className="flex flex-col gap-3 mt-8">
            <Link to="/login"><Button className="w-full rounded-full bg-[#2E523A] hover:bg-[#26442f] h-12" data-testid="check-email-go-login">Go to sign in</Button></Link>
            <button onClick={resend} className="text-sm font-semibold text-[#2E523A] hover:underline" data-testid="check-email-resend">
              Didn't get it? Resend email
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-12 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8"><Compass size={26} weight="duotone" className="text-[#2E523A]" /><span className="font-display text-xl font-bold">TravelNext</span></Link>
          <h1 className="font-display text-3xl font-bold">Create your account</h1>
          <p className="text-[#63675E] mt-1 mb-8">Already a member? <Link to="/login" className="text-[#2E523A] font-semibold" data-testid="go-login">Sign in</Link></p>

          <form onSubmit={submit} className="space-y-5">
            {error && <div className="text-sm text-destructive bg-destructive/10 rounded-lg px-4 py-3" data-testid="register-error">{error}</div>}
            <div className="space-y-2">
              <Label>Full name</Label>
              <Input required value={name} onChange={(e) => { setName(e.target.value); setError(""); }} placeholder="Jane Traveller" className="h-12" data-testid="register-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" required value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }} placeholder="you@example.com" className="h-12" data-testid="register-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input type="password" required minLength={6} value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }} placeholder="At least 6 characters" className="h-12" data-testid="register-password-input" />
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#2E523A] hover:bg-[#26442f] h-12 text-base" data-testid="register-submit-btn">
              {busy ? "Creating…" : "Create account"}
            </Button>
            <p className="flex items-center justify-center gap-1.5 text-xs text-[#63675E]">
              <CheckCircle size={14} weight="duotone" className="text-[#2E523A]" /> We'll email you a link to confirm your address
            </p>
          </form>
        </div>
      </div>

      <div className="relative hidden lg:block order-1 lg:order-2">
        <img src={HERO} alt="Patagonia" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#1A1C19]/40" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white items-end text-right">
          <Link to="/" className="flex items-center gap-2"><span className="font-display text-2xl font-bold">TravelNext</span><Compass size={28} weight="duotone" className="text-[#E8B291]" /></Link>
          <div>
            <h2 className="font-display text-4xl font-bold leading-tight">Start planning<br />the extraordinary.</h2>
            <p className="text-white/80 mt-3 max-w-sm ml-auto">Save trips, chat with AI and shop gear — all in one account.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
